import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { PublicPageShell } from '@/components/auth/PublicPageShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  PasswordFields,
  usePasswordStrength,
} from '@/features/settings/components/PasswordFields';
import { AccountAccessError, recoverPassword } from '../account-access.api';
import { RecoverFormValues, recoverSchema } from '../account-access.schemas';

const TOKEN_HINT = 'Request a new link from the "Forgot password" page.';

/**
 * Landing page of the password-recovery and admin password-reset mails
 * (`/recover?token=`). Sets a new password, then sends the user to sign in.
 */
export function RecoverPasswordPage() {
  const navigate = useNavigate();
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [error, setError] = useState<string | null>(null);

  const form = useForm<RecoverFormValues>({
    resolver: zodResolver(recoverSchema),
    defaultValues: { password: '', repeatPassword: '' },
  });
  const password = form.watch('password');
  const { strength, isStrongEnough } = usePasswordStrength(password);

  const onSubmit = async (values: RecoverFormValues) => {
    if (!token) return;
    setError(null);
    if (!(await isStrongEnough(values.password))) {
      form.setError('password', {
        message: 'Please choose a stronger password',
      });
      return;
    }
    try {
      await recoverPassword(token, values.password);
      toast.success('Your password has been changed', {
        description: 'You can now sign in with your new password.',
      });
      navigate({ to: '/login', replace: true });
    } catch (e) {
      if (e instanceof AccountAccessError && /password/i.test(e.message)) {
        form.setError('password', {
          message: 'Please choose a stronger password',
        });
      } else if (e instanceof AccountAccessError && e.status === 403) {
        setError(`This link has expired. ${TOKEN_HINT}`);
      } else if (e instanceof AccountAccessError && e.status === 404) {
        setError(
          `This link is invalid or has already been used. ${TOKEN_HINT}`,
        );
      } else {
        setError('Something went wrong. Please try again later.');
      }
    }
  };

  if (!token) {
    return (
      <PublicPageShell title="Choose a new password">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This link is incomplete. {TOKEN_HINT}
          </AlertDescription>
        </Alert>
      </PublicPageShell>
    );
  }

  const { isSubmitting } = form.formState;

  return (
    <PublicPageShell
      title="Choose a new password"
      description="Pick a strong password you do not use anywhere else"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}{' '}
                <Link to="/forgot" className="underline">
                  Forgot password
                </Link>
              </AlertDescription>
            </Alert>
          )}
          <PasswordFields form={form} strength={strength} />
          <Button
            type="submit"
            className="w-full"
            disabled={!password || isSubmitting}
          >
            {isSubmitting && <Loader2 className="animate-spin" />}
            Change password
          </Button>
        </form>
      </Form>
    </PublicPageShell>
  );
}
